'use client';
import { useState } from 'react';
import {
	LoadingIndicator,
	Link,
	OneTrustScript,
	ErrorAlert,
	WebChatButton,
	Modal,
} from '@fshr/components';
import { OTPForm } from '@profiles/components';
import { useI18n } from '@fshr/context/i18n-context';
import useSWR from 'swr';
import { defaultFetcher } from '@fshr/shared';
import type { ContentV1 } from '@fshr/microservices/content';
import sortBy from 'lodash/sortBy';
import { getTollFreeNumbersLink } from '@fshr/shared-react';
import FindAReservationForm from '../_components/find-a-reservation-form';
import { useParams, useRouter } from 'next/navigation';
import { useProfile } from '@profiles/lib/context/profile-context';
import { useEffect } from 'react';
import { isContentSegment, whitelistedSegments } from '../../upcoming-trip/_helpers/segments';
import { UserPanelTabId } from '@fshr/shared/types';

const FindAReservationPage = () => {
	const router = useRouter();
	const { isInitializing, isAuthenticated, globalStore } = useProfile();
	const params = useParams();
	const { t, localeLink, locale } = useI18n();
	const {
		data: pageData,
		isLoading,
		error,
	} = useSWR(
		localeLink(`${process.env.NEXT_PUBLIC_BFF_BASE_URL}/properties-by-region/`),
		defaultFetcher,
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
		}
	);

	const hotelOptions: { value: string; label: string; propertyId?: string }[] = sortBy(
		pageData?.propertiesByRegion?.reduce(
			(
				agg: {
					owsCode?: string;
					label?: string;
					sortingName?: string;
				}[],
				{ properties }: ContentV1.PropertiesByRegionRegion
			) => {
				return [
					...agg,
					...properties.map(({ bookingConfig, shortName, sortingName, id }) => {
						return {
							owsCode: bookingConfig?.owsCode,
							label: shortName,
							sortingName: sortingName || shortName,
							propertyId: id,
						};
					}),
				];
			},
			[]
		),
		'sortingName'
	).map(({ owsCode, label, propertyId }) => {
		return {
			value: owsCode,
			label,
			propertyId,
		};
	});

	const hotelName = params.details?.[0] || '';
	const confirmationNumber = params.details?.[1] || '';
	const additionalRouterSegment =
		params.details?.[2] && whitelistedSegments.includes(params.details?.[2])
			? '/' + params.details?.[2]
			: '';

	useEffect(() => {
		const hasContentSegments = isContentSegment(additionalRouterSegment);
		const isItinerarySegment = additionalRouterSegment?.replace('/', '') === 'itinerary';
		if (!isInitializing && isAuthenticated && !hasContentSegments) {
			if (hotelName && confirmationNumber) {
				if (isItinerarySegment) {
					globalStore?.setUserPanelOpenTab(UserPanelTabId.ITINERARY);
					router.replace(
						`${localeLink(
							`/profile/my-stays/${hotelName}/${confirmationNumber}`
						)}${window.location.search}#itinerary`
					);
				} else {
					router.push(
						`${localeLink(
							`/profile/my-stays/${hotelName}/${confirmationNumber}${additionalRouterSegment}`
						)}${window.location.search}`
					);
				}
			} else {
				router.push(`${localeLink('/profile/my-stays')}${window.location.search}`);
			}
			return;
		}

		if (
			hotelOptions &&
			hotelOptions.length > 0 &&
			hotelName &&
			globalStore &&
			!globalStore.fetching &&
			globalStore.activeBookingId
		) {
			//Redirect to content segment
			const { propertyId } =
				hotelOptions.find(hotelOption => hotelOption.value === hotelName) ?? {};
			if (propertyId && hasContentSegments) {
				router.push(
					`${localeLink(`/${propertyId}${additionalRouterSegment}`)}${window.location.search}`
				);
				return;
			}
		}
	}, [
		pageData,
		isInitializing,
		isAuthenticated,
		hotelName,
		confirmationNumber,
		hotelOptions,
		globalStore,
		globalStore?.fetching,
		globalStore?.activeBookingId,
		additionalRouterSegment,
	]);

	const [showSignInModal, setShowSignInModal] = useState(false);
	const [pendingWebChatOpen, setPendingWebChatOpen] = useState(false);
	const [lockModal, setLockModal] = useState(false);

	const handleOpenSignInModal = () => {
		setShowSignInModal(true);
	};

	const handleCloseSignInModal = () => {
		setShowSignInModal(false);
	};

	const handleSignInSuccess = () => {
		setPendingWebChatOpen(true);
	};

	const handleReadyWebChat = () => {
		if (pendingWebChatOpen) {
			setTimeout(() => {
				window.dispatchEvent(new Event('webchat.open'));
			}, 500); // Delay to ensure the webchat is ready
			setPendingWebChatOpen(false);
		}
	};

	if (!pageData && !isLoading) {
		if (error) {
			console.error('swr error: showed error', error);
		}
		return <ErrorAlert />;
	}

	if (error) {
		console.error('swr error: showed cached result', error);
	}

	return (
		<div className="container mx-auto lg:max-w-[610px] xl:max-w-[930px] mt-[80px] lg:mt-[60px] mb-[70px] px-[15px] md:px-0 overflow-x-hidden">
			<OneTrustScript />
			{(isInitializing || isLoading) && <LoadingIndicator />}
			{!isInitializing && !isAuthenticated && !isLoading && (
				<div className="flex flex-col gap-300">
					<div className="text-center">
						<h2 className="text-h2 mb-200 lg:mb-400">{t('Find A Reservation')}</h2>
						<span className="text-center text-body2">
							{t('Please enter your reservation details.')}
						</span>
					</div>
					<FindAReservationForm
						hotelOptions={hotelOptions}
						additionalRouterSegment={additionalRouterSegment}
						initialValues={{
							hotelName,
							confirmationNumber,
						}}
					/>
					<div className="flex flex-row items-center gap-[25px]">
						<div className="border-b border-b-border-subtle w-full"></div>
						<span className="text-body2">{t('Or')}</span>
						<div className="border-b border-b-border-subtle w-full"></div>
					</div>
					<div className="flex flex-row gap-200 w-full">
						<Link intent="outline" className="w-full" href={localeLink('/sign-in')}>
							{t('Sign In')}
						</Link>
						<Link intent="outline" className="w-full" href={localeLink('/register')}>
							{t('Register')}
						</Link>
					</div>
					<div className="flex flex-col gap-200 w-full mt-200 lg:mt-300">
						<span className="text-label">{t('Get in Touch')}</span>
						<div className="flex flex-col items-start justify-start gap-100 lg:w-full lg:justify-between lg:flex-row ">
							<span className="text-overline">{t('For further reservation assistance')}</span>
							<Link intent="text" href={getTollFreeNumbersLink(locale)}>
								{t('Toll Free Numbers by Country')}
							</Link>
						</div>
					</div>
				</div>
			)}
			{globalStore && (
				<WebChatButton
					locale={locale}
					globalStore={globalStore}
					onSignInRequest={handleOpenSignInModal}
					onReadyWebChat={handleReadyWebChat}
				/>
			)}

			{showSignInModal && (
				<Modal close={lockModal ? undefined : () => handleCloseSignInModal()}>
					<div className="flex w-full h-full flex-col justify-start items-center">
						<div className="w-full flex-1 flex justify-center items-center">
							<OTPForm
								onSuccess={() => handleSignInSuccess()}
								lockModal={() => setLockModal(true)}
								unlockModal={() => setLockModal(false)}
								loadingIndicatorClasses="absolute"
								additionalRequestOtpClasses={{
									nextButtonAdditionalClasses: 'w-[130px]',
								}}
							/>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default FindAReservationPage;
